import { useNamespaceCopy } from '@/i18n/ns';
import panelsCopy from '@/i18n/ns/artifact-panels';
import meetingsRepairCopy from '@/i18n/ns/meetings-repair';
import type { SavedChartDisplay } from '../../../../src/artifacts/saved-view-contract.mjs';
import type { Scalar } from '../models';
import type { Cell, TablePayload } from '../../types';
import { TableView } from '../table/table-view';
import type { ViewerProps } from '../document/blocks';
import { EnhancedWorkspaceChart } from './view';
import type { ChartInput, EnhancedChartOptions } from './types';

function plotValue(value: Scalar): Cell {
  if (value.kind === 'null') return null;
  return value.kind === 'decimal' ? Number(value.value) : value.value;
}

/** One literal Scalar conversion for the saved canvas and server export. */
export function savedChartInput(
  display: SavedChartDisplay,
  locale: string | undefined,
  labels: EnhancedChartOptions['labels'],
): ChartInput {
  const rows = display.table.rows.map((row) =>
    Object.fromEntries(
      display.table.columns.map((column) => [column.id, plotValue(row.cells[column.id].value)]),
    ),
  );
  const columns: TablePayload['columns'] = display.table.columns.map((column) => ({
    key: column.id,
    label: column.label,
    type: column.currency
      ? 'money'
      : ['integer', 'decimal'].includes(column.type)
        ? 'number'
        : 'text',
    ...(column.currency ? { currency: column.currency } : {}),
    precision: Math.max(
      0,
      ...display.table.rows.map((row) => {
        const value = row.cells[column.id].value;
        return value.kind === 'decimal' ? value.scale : 0;
      }),
    ),
  }));
  return {
    chart: {
      type: display.chartType === 'line' ? 'line' : 'bar',
      xKey: display.xColumnId,
      yKeys: display.yColumnIds,
      rows,
    },
    columns,
    enhanced: {
      summary: display.title,
      entityOrder: display.yColumnIds,
      series: display.yColumnIds.map((key) => ({
        key,
        entityId: key,
        label: columns.find((column) => column.key === key)!.label,
      })),
      labels,
      horizontal: display.chartType === 'bar',
      stacked: display.chartType === 'stacked-column',
      locale,
    },
  };
}

/** The table alternative preserves the original precision and cell identities. */
export function SavedChartDisplayView({
  display,
  locale,
  className,
  style,
}: ViewerProps & { display: SavedChartDisplay }) {
  const panels = useNamespaceCopy(panelsCopy);
  const repair = useNamespaceCopy(meetingsRepairCopy);
  const input = savedChartInput(display, locale, {
    showTable: panels.kindTable,
    hideTable: panels.kindTable,
    loading: panels.panelLoading,
    unavailable: panels.panelError,
    other: repair.kindOther,
  });
  const table = <TableView display={display.table} locale={locale} />;
  const finite = input.chart.rows.every((row) =>
    Object.values(row).every((value) => typeof value !== 'number' || Number.isFinite(value)),
  );
  return (
    <section
      data-kind="chart"
      data-inspect-node={display.id}
      aria-label={display.title}
      className={className ? `ws-table-page ${className}` : 'ws-table-page'}
      style={style}
    >
      <h1 className="ws-doc-title">{display.title}</h1>
      {finite ? (
        <EnhancedWorkspaceChart {...input} table={table} />
      ) : (
        <>
          <p role="status">{panels.panelError}</p>
          {table}
        </>
      )}
    </section>
  );
}
